import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface WaitlistTestProps {
  classId: string;
  organizationId: string;
}

export const WaitlistTestComponent: React.FC<WaitlistTestProps> = ({
  classId,
  organizationId
}) => {
  const [waitlistId, setWaitlistId] = useState<string | null>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWaitlist();
  }, [classId]);

  const fetchWaitlist = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // First, get or create the waitlist
      const { data: waitlistData, error: waitlistError } = await supabase
        .from('waitlists')
        .select('id')
        .eq('class_id', classId)
        .single();

      if (waitlistError && waitlistError.code !== 'PGRST116') {
        throw waitlistError;
      }

      let wlId = waitlistData?.id;

      // If waitlist doesn't exist yet, create it
      if (!wlId) {
        const { data: newWaitlist, error: createError } = await supabase
          .from('waitlists')
          .insert({
            class_id: classId,
            organization_id: organizationId,
          })
          .select('id')
          .single();

        if (createError) throw createError;
        wlId = newWaitlist.id;
      }

      setWaitlistId(wlId);

      // Now get the waitlist entries with student details
      const { data, error: entriesError } = await supabase
        .rpc('get_waitlist_entries_with_details', {
          waitlist_id_param: wlId
        });

      if (entriesError) throw entriesError;
      
      // Ensure data is an array before setting state
      setEntries(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error fetching waitlist:', error);
      setError(error.message || 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Waitlist Test</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div>Loading waitlist data...</div>
        ) : error ? (
          <div className="text-red-500">
            Error: {error}
            <Button className="ml-4" onClick={fetchWaitlist}>Retry</Button>
          </div>
        ) : (
          <div>
            <div>Waitlist ID: {waitlistId || 'None'}</div>
            <div>Entries found: {entries.length}</div>
            
            {entries.length > 0 ? (
              <pre className="mt-4 bg-gray-100 p-4 rounded-md overflow-auto text-xs">
                {JSON.stringify(entries, null, 2)}
              </pre>
            ) : (
              <div className="mt-4">No waitlist entries found.</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WaitlistTestComponent; 